import { Typography, Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { getContactInfo, hasContactDetails } from "./utils"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { Organizer } from "../../../../../../../shared/types/OrganizerTypes"
import { getOrganizerPhone } from "../../../../../../../shared/utils/getOrderPerson"
import { TranslationKey } from "../../../../../../localization/translations"
import { ContactInfoDisclaimer } from "../../../../../../../shared/components/receipt/ContactInfoDisclaimer"

const useStyles = makeStyles()((theme) => ({
    contactSection: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            marginTop: theme.spacing(3),
        },
    },
    contactLabel: {
        lineHeight: "1.5rem",
        marginBottom: theme.spacing(0.5),
        color: theme.palette.tailwind.slate[600],
        [theme.breakpoints.down("sm")]: {
            marginBottom: 0,
        },
    },
    contactName: {
        fontWeight: 500,
        textAlign: "right",
        color: theme.palette.tailwind.slate[900],
        [theme.breakpoints.down("sm")]: {
            textAlign: "left",
            wordBreak: "break-word",
            fontSize: theme.typography.body1.fontSize,
        },
    },
    contactInfo: {
        textAlign: "right",
        color: theme.palette.tailwind.slate[900],
        [theme.breakpoints.down("sm")]: {
            textAlign: "left",
        },
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        color: theme.palette.tailwind.slate[700],
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down("sm")]: {
            marginBottom: theme.spacing(2),
            fontWeight: 500,
            color: theme.palette.tailwind.slate[800],
        },
    },

    contactRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            gap: theme.spacing(0.25),
            marginBottom: theme.spacing(2),
            "&:last-child": {
                marginBottom: 0,
            },
        },
        "&:last-child": {
            marginBottom: 0,
        },
    },
    contactEmailAndPhone: {
        color: theme.palette.tailwind.slate[900],
        fontWeight: 400,
    },
}))

interface DetailRowProps {
    creator: Organizer
    label: string
}

const DetailRow = ({ creator, label }: DetailRowProps) => {
    const { classes } = useStyles()
    const phone = getOrganizerPhone(creator)

    return (
        <Box className={classes.contactRow}>
            <Typography variant="body1" className={classes.contactLabel}>
                {label}
            </Typography>
            <Box className={classes.contactInfo}>
                <Typography variant="body1" className={classes.contactName}>
                    {creator.name}
                </Typography>

                {creator.email && (
                    <Typography variant="body1" className={classes.contactEmailAndPhone}>
                        {creator.email}
                    </Typography>
                )}
                {phone && (
                    <Typography variant="body1" className={classes.contactEmailAndPhone}>
                        {phone}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

interface ContactDetailsProps {
    order: MeetingDetailsOrder
}

export default function ContactDetails({ order }: ContactDetailsProps) {
    const { classes } = useStyles()
    const contactInfo = getContactInfo(order.organizers, order.customer)
    const { creator, meetingHost, showBoth } = contactInfo

    return (
        <Box className={classes.contactSection}>
            <LocalizedStrict id="production-package-slip-section-contact-details-title">
                <Typography variant="h6" className={classes.sectionTitle}>
                    Contact details
                </Typography>
            </LocalizedStrict>
            {showBoth && creator && (
                <LocalizedStrict
                    id="production-package-slip-section-contact-details-meeting-creator"
                    attrs={{ label: true }}
                >
                    <DetailRow label="Creator:" creator={creator} />
                </LocalizedStrict>
            )}
            {meetingHost && (
                <LocalizedStrict
                    id="production-package-slip-section-contact-details-meeting-host"
                    attrs={{ label: true }}
                >
                    <DetailRow label="Meeting host:" creator={meetingHost} />
                </LocalizedStrict>
            )}
            {hasContactDetails(contactInfo) && <ContactInfoDisclaimer />}
        </Box>
    )
}
